import React, { createContext, FC, memo, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from "@reach/router";

import { useSafeContext } from 'hooks/useSafeContext/useSafeContext';
import { useCheckoutSessionContext } from '../checkoutSession';
import { useGiftCards } from '../giftcards';
import { calculatePendingAmountForGiftCards } from './utils';
import { useMerchantPaymentConfigContext } from '../merchantPaymentConfig';

export type CalcState = {
  isVariableAmount: boolean;
  surchargeRate?: number;
  payableTotalAmount: number;
  convenienceFee: number;
  minAmount?: number;
  tipAmount: number;
  setPayableTotalAmount: (total: number) => void;
  setTipAmount: (tip: number) => void;
  pendingAmount: number;
  setPendingAmount: (newPendingAmount: number) => void;
  sourceAmounts: number[];
  setSourceAmounts: (newSourceAmounts: number[]) => void;
};

const CalcContext = createContext<CalcState | undefined>(undefined);
// eslint-disable-next-line
CalcContext.displayName = 'Calc';

export const CalcProvider: FC<RouteComponentProps<{checkoutSessionId: Uuid}>> = memo(function CalcProvider({ children, checkoutSessionId }) {
  const [payableTotalAmount, setPayableTotalAmount] = useState<number>(0);
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [isVariableAmount, setIsVariableAmount] = useState<boolean>();
  const [pendingAmount, setPendingAmount] = useState<number>(0);
  const [sourceAmounts, setSourceAmounts] = useState<number[]>([]);
  const [surchargeRate, setSurchargeRate] = useState<number>();
  const { checkoutSession } = useCheckoutSessionContext();
  const { merchantPaymentConfig, getMerchantPaymentConfig } = useMerchantPaymentConfigContext();
  const { giftCards } = useGiftCards();
  const { shoppingCart, merchant, additionalCharges } = checkoutSession || {};
  const { total: cartTotal = 0, tipAmount: cartTipAmount = 0, minAmount = 0 } = shoppingCart || {};

  // TODO: Create a giftcard context to maintain giftcards list
  // TODO: use the giftcard context and giftcard reddeamble amounts here and keep all calculations in 1 provider.
  useEffect(() => {
    checkoutSessionId && cartTipAmount && setTipAmount(cartTipAmount);
  }, [checkoutSessionId, cartTipAmount]);

  const additionalChargeFee = useMemo(() => {
    const isMerchantSurchargable = merchantPaymentConfig?.surcharging?.supported;
    let convenienceFee = 0, surchargableFee = 0;

    for(const additionalCharge of additionalCharges ?? []) {
      const { type, amount = 0 } = additionalCharge;
      if (!isMerchantSurchargable && type === 'CONVENIENCE_FEE') {
        convenienceFee = amount;
        break;
      } else if (isMerchantSurchargable && type === 'CREDIT_SURCHARGE') {
        surchargableFee = amount;
        break;
      }
    }

    return {
      convenienceFee,
      surchargableFee
    }
  }, [additionalCharges, merchantPaymentConfig?.surcharging?.supported])

  const updatedTotal = useMemo(() => (
    (cartTotal || minAmount) + additionalChargeFee.convenienceFee + additionalChargeFee.surchargableFee + cartTipAmount
  ), [cartTotal, additionalChargeFee, cartTipAmount]);

  useEffect(() => {
    setIsVariableAmount(cartTotal === 0);
    setPendingAmount(updatedTotal);
  }, [cartTotal, updatedTotal]);

  useEffect(() => {
    if (checkoutSessionId && isVariableAmount !== undefined) {
      if (!isVariableAmount) setPayableTotalAmount(cartTotal);
      else setPayableTotalAmount(minAmount);
    }
  }, [checkoutSessionId, cartTotal, isVariableAmount, minAmount]);

  // Get the sum of all the gift card amounts and
  useEffect(() => {
    // Calculate pending amount based on current giftcards and total.
    const newPendingAmount = calculatePendingAmountForGiftCards(giftCards, updatedTotal);
    setPendingAmount(newPendingAmount);
  }, [updatedTotal, giftCards]);

  useEffect(() => {
    merchant?.id && getMerchantPaymentConfig(merchant.id);
  }, [merchant?.id, getMerchantPaymentConfig]);

  useEffect(() => {
    if (merchantPaymentConfig?.surcharging?.supported) {
      setSurchargeRate(merchantPaymentConfig.surcharging.rate);
    }
  }, [merchantPaymentConfig?.surcharging]);

  const values = {
    convenienceFee: additionalChargeFee.convenienceFee,
    pendingAmount,
    surchargeRate,
    sourceAmounts,
    setPendingAmount,
    setSourceAmounts,
    isVariableAmount: !!isVariableAmount,
    payableTotalAmount,
    minAmount,
    tipAmount,
    setPayableTotalAmount,
    setTipAmount,
  };

  return (
    <CalcContext.Provider value={values}>
      {children}
    </CalcContext.Provider>
  );
});

export function useCalcContext() {
  return useSafeContext(useCalcContext, CalcContext);
}
